import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { type ClipboardEvent, type FocusEvent, type KeyboardEvent, type MouseEvent, type Ref, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import type { InlineMarkupRegistry } from '../inline-markup-registry';
import InlineText from '../inline-text';
type SubmitReason = 'enter' | 'blur';
type EditableTextProps = {
  value: string;
  registry: InlineMarkupRegistry;
  editable?: boolean;
  onSubmit: (next: string, reason: SubmitReason) => void;
  onEnter?: () => void;
  onAdvance?: () => void;
  editOnFocus?: boolean;
  ref?: Ref<{
    focus: () => void;
  }>;
  className?: string;
  placeholder?: string;
  // An optional field submits empty (removing the value) and collapses to a re-addable
  // affordance; a required field reverts a blank submit to its last value instead.
  optional?: boolean;
  // Multi-line fields (rationale, note) accept Enter as a newline; single-line
  // fields submit on Enter instead.
  multiline?: boolean;
  // Keep the field's semantic element (headings stay in the a11y outline, dt/dd keep the
  // definition-list semantics) in both modes.
  as?: 'span' | 'div' | 'p' | 'h2' | 'h3' | 'dt' | 'dd' | 'time';
  label?: string;
  // A revealed section mounts straight into the focused input (skipping the inline
  // affordance) and reports an empty blur so the block can collapse it again.
  autoFocus?: boolean;
  onEmptyBlur?: () => void;
  // Fill the container width so the whole padded bar is the click/focus target, not just
  // the width of the text — otherwise clicking the empty part of the bar blurs and collapses.
  block?: boolean;
  // A field whose value must stay on one line. Line breaks that arrive by paste become spaces, or
  // separate lines handed to `onSubmitLines` when it is set, so a list can turn them into rows.
  singleLine?: boolean;
  onSubmitLines?: (lines: string[], reason: SubmitReason) => void;
};
const LINE_BREAK = /\r\n|[\n\r\u0085\u2028\u2029]/;

// Pasted list lines keep their markers ("- a", "1. a", "- [ ] a"), which the compiled list would
// double up ("- - a"). Requiring a space after the marker leaves "1.5 mg" alone.
const LIST_MARKER = /^(?:[-*+•]\s+(?:\[[ xX]\]\s+)?|\d+[.)]\s+)/;

// Pasting into a contenteditable keeps the source's markup; a single-line field takes the plain text
// only, so its line breaks can be read back and dealt with on submit.
const insertPlainText = (element: HTMLElement, text: string) => {
  const selection = window.getSelection();
  const current = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  const range = current && element.contains(current.commonAncestorContainer) ? current : endOfContent(element);
  range.deleteContents();
  const inserted = document.createTextNode(text);
  range.insertNode(inserted);
  range.setStartAfter(inserted);
  range.collapse(true);
  selection?.removeAllRanges();
  selection?.addRange(range);
};

// Serialize an editable field back to its source string. Unlike reading `innerText`, this recovers each
// inline chip's verbatim token from `data-inline-raw` (byte-exact) so an untouched cite or pill
// round-trips whole — a chip only *shows* a short label (`S#`, a pill word), so rendered text would drop
// the token's real syntax. contentEditable line breaks come back as <div>/<br> nodes; treat each as a
// newline, matching the rendered breaks innerText used to give (textContent alone would collapse "a\nb"
// to "ab").
const serializeNode = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as HTMLElement;
  const verbatim = el.getAttribute('data-inline-raw');
  if (verbatim) return verbatim;
  if (el.tagName === 'BR') return '\n';
  const inner = Array.from(el.childNodes, serializeNode).join('');
  // Chrome wraps each line after the first in its own <div>; that boundary is a line break.
  return el.tagName === 'DIV' ? `\n${inner}` : inner;
};
const endOfContent = (element: HTMLElement): Range => {
  const range = document.createRange();
  range.selectNodeContents(element);
  range.collapse(false);
  return range;
};

/**
 * A collapsed range at a viewport point, or null when the point isn't over this element's text.
 *
 * Two spellings: `caretPositionFromPoint` is the standard, `caretRangeFromPoint` is what WebKit and
 * older Chrome expose. Neither exists in jsdom, so tests take the end-of-content path.
 */
const rangeFromPoint = (element: HTMLElement, x: number, y: number): Range | null => {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => {
      offsetNode: Node;
      offset: number;
    } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  let range: Range | null = null;
  if (typeof doc.caretPositionFromPoint === 'function') {
    const position = doc.caretPositionFromPoint(x, y);
    if (position) {
      range = document.createRange();
      range.setStart(position.offsetNode, position.offset);
      range.collapse(true);
    }
  } else if (typeof doc.caretRangeFromPoint === 'function') {
    range = doc.caretRangeFromPoint(x, y);
  }

  // A press on the field's padding resolves to a node outside it; the caller falls back to the end.
  return range && element.contains(range.startContainer) ? range : null;
};
const serializeEditableText = (el: HTMLElement) => Array.from(el.childNodes, serializeNode).join('').replace(/\r\n/g, '\n');
const EditableText = t0 => {
  const $ = _c(83);
  const {
    value,
    registry,
    editable: t1,
    onSubmit,
    onEnter,
    onAdvance,
    editOnFocus: t2,
    ref: forwardedRef,
    className,
    placeholder,
    optional: t3,
    multiline: t4,
    as: t5,
    label,
    autoFocus: t6,
    onEmptyBlur,
    block: t7,
    singleLine: t8,
    onSubmitLines
  } = t0;
  const editable = t1 === undefined ? false : t1;
  const editOnFocus = t2 === undefined ? false : t2;
  const optional = t3 === undefined ? false : t3;
  const multiline = t4 === undefined ? false : t4;
  const as = t5 === undefined ? "span" : t5;
  const autoFocus = t6 === undefined ? false : t6;
  const block = t7 === undefined ? false : t7;
  const singleLine = t8 === undefined ? false : t8;
  const ref = useRef(null);
  const [editing, setEditing] = useState(autoFocus);
  const [editKey, setEditKey] = useState(0);
  let t9;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => ({
      focus: () => {
        setEditing(true);
        ref.current?.focus();
      }
    });
    $[0] = t9;
  } else {
    t9 = $[0];
  }
  useImperativeHandle(forwardedRef, t9);
  let t10;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setEditKey(_temp);
    };
    $[1] = t10;
  } else {
    t10 = $[1];
  }
  const remount = t10;
  const pointerFocus = useRef(false);
  const caretAt = useRef(null);
  const commitReason = useRef("blur");
  let t11;
  let t12;
  if ($[2] !== editing) {
    t11 = () => {
      if (!editing) {
        return;
      }
      const element = ref.current;
      if (!element) {
        return;
      }
      element.focus();
      const point = caretAt.current;
      caretAt.current = null;
      const selection = window.getSelection();
      if (!selection) {
        return;
      }
      const fromPress = point && rangeFromPoint(element, point.x, point.y);
      selection.removeAllRanges();
      selection.addRange(fromPress ?? endOfContent(element));
    };
    t12 = [editing];
    $[2] = editing;
    $[3] = t11;
    $[4] = t12;
  } else {
    t11 = $[3];
    t12 = $[4];
  }
  useLayoutEffect(t11, t12);
  let t13;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      remount();
      setEditing(false);
    };
    $[5] = t13;
  } else {
    t13 = $[5];
  }
  const revert = t13;
  const Tag = as;
  let T0;
  let handleKeyDown;
  let handleMouseDown;
  let handlePaste;
  let submit;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  if ($[6] !== Tag || $[7] !== block || $[8] !== className || $[9] !== editKey || $[10] !== editOnFocus || $[11] !== editable || $[12] !== editing || $[13] !== label || $[14] !== multiline || $[15] !== onAdvance || $[16] !== onEmptyBlur || $[17] !== onEnter || $[18] !== onSubmit || $[19] !== onSubmitLines || $[20] !== optional || $[21] !== placeholder || $[22] !== registry || $[23] !== singleLine || $[24] !== value) {
    t18 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const textClassName = cx(multiline && "whitespace-pre-wrap", className);
      let t19;
      if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
        t19 = event => {
          if ((event.target as HTMLElement | null)?.closest("[data-inline-interactive]")) {
            return;
          }
          caretAt.current = {
            x: event.clientX,
            y: event.clientY
          };
          setEditing(true);
        };
        $[35] = t19;
      } else {
        t19 = $[35];
      }
      const startEditing = t19;
      let t20;
      if ($[36] !== editOnFocus) {
        t20 = event_0 => {
          if (event_0.target !== event_0.currentTarget) {
            return;
          }
          if (editOnFocus && !pointerFocus.current) {
            setEditing(true);
          }
          pointerFocus.current = false;
        };
        $[36] = editOnFocus;
        $[37] = t20;
      } else {
        t20 = $[37];
      }
      const handleFocus = t20;
      let t21;
      if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
        t21 = event_1 => {
          pointerFocus.current = true;
          event_1.stopPropagation();
        };
        $[38] = t21;
      } else {
        t21 = $[38];
      }
      handleMouseDown = t21;
      let t22;
      if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
        t22 = event_2 => {
          if (event_2.target !== event_2.currentTarget || event_2.key !== "Enter" && event_2.key !== " ") {
            return;
          }
          event_2.preventDefault();
          event_2.stopPropagation();
          setEditing(true);
        };
        $[39] = t22;
      } else {
        t22 = $[39];
      }
      const handleReadKeyDown = t22;
      if (!editable) {
        let t23;
        if ($[40] !== registry || $[41] !== value) {
          t23 = <InlineText registry={registry} text={value} />;
          $[40] = registry;
          $[41] = value;
          $[42] = t23;
        } else {
          t23 = $[42];
        }
        t18 = <Tag className={textClassName}>{t23}</Tag>;
        break bb0;
      }
      if (optional && !value && !editing) {
        let t23;
        if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
          t23 = () => setEditing(true);
          $[43] = t23;
        } else {
          t23 = $[43];
        }
        let t24;
        if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
          t24 = () => {
            pointerFocus.current = false;
          };
          $[44] = t24;
        } else {
          t24 = $[44];
        }
        t18 = <button type="button" className={cx("block w-full cursor-text text-left text-quaternary opacity-0 outline-none transition-opacity group-hover:opacity-100 focus:opacity-100", textClassName)} onClick={t23} data-editable-text-field={true} onFocus={handleFocus} onMouseDown={handleMouseDown} onMouseUp={t24}>{placeholder}</button>;
        break bb0;
      }
      if (!editing) {
        let t23;
        if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
          t23 = () => {
            pointerFocus.current = false;
          };
          $[45] = t23;
        } else {
          t23 = $[45];
        }
        let t24;
        if ($[46] !== registry || $[47] !== value) {
          t24 = <InlineText registry={registry} text={value} />;
          $[46] = registry;
          $[47] = value;
          $[48] = t24;
        } else {
          t24 = $[48];
        }
        t18 = <Tag key={editKey} aria-label={label} aria-readonly={true} className={cx("cursor-text outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent", textClassName)} onClick={startEditing} data-editable-text-field={true} onFocus={handleFocus} onKeyDown={handleReadKeyDown} onMouseDown={handleMouseDown} onMouseUp={t23} role="textbox" tabIndex={0}>{t24}</Tag>;
        break bb0;
      }
      const oneLine = singleLine || !!onSubmitLines;
      let t23;
      if ($[49] !== onEmptyBlur || $[50] !== onSubmit || $[51] !== onSubmitLines || $[52] !== oneLine || $[53] !== optional || $[54] !== value) {
        t23 = () => {
          const text = ref.current ? serializeEditableText(ref.current) : "";
          const lines = oneLine ? text.split(LINE_BREAK).map(_temp2).filter(Boolean) : [];
          const next = oneLine ? lines.join(" ") : text.trim();
          const reason = commitReason.current;
          commitReason.current = "blur";
          remount();
          setEditing(false);
          if (onSubmitLines && lines.length > 1) {
            onSubmitLines(lines.map(_temp3).filter(Boolean), reason);
            return;
          }
          if (!next && !optional) {
            revert();
            return;
          }
          if (!next) {
            onEmptyBlur?.();
          }
          if (next !== value) {
            onSubmit(next, reason);
          }
        };
        $[49] = onEmptyBlur;
        $[50] = onSubmit;
        $[51] = onSubmitLines;
        $[52] = oneLine;
        $[53] = optional;
        $[54] = value;
        $[55] = t23;
      } else {
        t23 = $[55];
      }
      submit = t23;
      let t24;
      if ($[56] !== multiline || $[57] !== onAdvance || $[58] !== onEmptyBlur || $[59] !== onEnter || $[60] !== optional || $[61] !== value) {
        t24 = event_3 => {
          event_3.stopPropagation();
          if (event_3.nativeEvent.isComposing) {
            return;
          }
          const advances = onAdvance && !event_3.shiftKey && !event_3.altKey && !event_3.ctrlKey && !event_3.metaKey && (event_3.key === "Tab" || event_3.key === "Enter" && !multiline) && ref.current && serializeEditableText(ref.current).trim();
          if (advances) {
            event_3.preventDefault();
            commitReason.current = event_3.key === "Enter" ? "enter" : "blur";
            ref.current?.blur();
            onAdvance();
            return;
          }
          if (event_3.key === "Enter" && !multiline) {
            event_3.preventDefault();
            commitReason.current = "enter";
            ref.current?.blur();
            onEnter?.();
          }
          if (event_3.key === "Escape") {
            event_3.preventDefault();
            revert();
            if (!value && optional) {
              onEmptyBlur?.();
            }
          }
        };
        $[56] = multiline;
        $[57] = onAdvance;
        $[58] = onEmptyBlur;
        $[59] = onEnter;
        $[60] = optional;
        $[61] = value;
        $[62] = t24;
      } else {
        t24 = $[62];
      }
      handleKeyDown = t24;
      let t25;
      if ($[63] !== onSubmitLines || $[64] !== oneLine) {
        t25 = event_4 => {
          if (!oneLine || !ref.current) {
            return;
          }
          event_4.preventDefault();
          const text_0 = event_4.clipboardData.getData("text/plain");
          insertPlainText(ref.current, text_0);
          if (onSubmitLines && text_0.split(LINE_BREAK).filter(_temp4).length > 1) {
            ref.current.blur();
          }
        };
        $[63] = onSubmitLines;
        $[64] = oneLine;
        $[65] = t25;
      } else {
        t25 = $[65];
      }
      handlePaste = t25;
      T0 = Tag;
      t14 = editKey;
      t15 = ref as never;
      t16 = label;
      t17 = cx("cursor-text outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent empty:before:text-quaternary empty:before:content-[attr(data-placeholder)]", {
        "block w-full": block
      }, textClassName);
    }
    $[6] = Tag;
    $[7] = block;
    $[8] = className;
    $[9] = editKey;
    $[10] = editOnFocus;
    $[11] = editable;
    $[12] = editing;
    $[13] = label;
    $[14] = multiline;
    $[15] = onAdvance;
    $[16] = onEmptyBlur;
    $[17] = onEnter;
    $[18] = onSubmit;
    $[19] = onSubmitLines;
    $[20] = optional;
    $[21] = placeholder;
    $[22] = registry;
    $[23] = singleLine;
    $[24] = value;
    $[25] = T0;
    $[26] = handleKeyDown;
    $[27] = handleMouseDown;
    $[28] = handlePaste;
    $[29] = submit;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
  } else {
    T0 = $[25];
    handleKeyDown = $[26];
    handleMouseDown = $[27];
    handlePaste = $[28];
    submit = $[29];
    t14 = $[30];
    t15 = $[31];
    t16 = $[32];
    t17 = $[33];
    t18 = $[34];
  }
  if (t18 !== Symbol.for("react.early_return_sentinel")) {
    return t18;
  }
  const t19 = onAdvance ? "" : undefined;
  let t20;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      pointerFocus.current = false;
    };
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] !== registry || $[68] !== value) {
    t21 = <InlineText editable={true} registry={registry} text={value} />;
    $[67] = registry;
    $[68] = value;
    $[69] = t21;
  } else {
    t21 = $[69];
  }
  let t22;
  if ($[70] !== T0 || $[71] !== handleKeyDown || $[72] !== handleMouseDown || $[73] !== handlePaste || $[74] !== placeholder || $[75] !== submit || $[76] !== t14 || $[77] !== t15 || $[78] !== t16 || $[79] !== t17 || $[80] !== t19 || $[81] !== t21) {
    t22 = <T0 key={t14} ref={t15} aria-label={t16} className={t17} contentEditable={true} suppressContentEditableWarning={true} data-placeholder={placeholder} data-editable-text-field={true} data-advance-on-tab={t19} onBlur={submit} onKeyDown={handleKeyDown} onMouseDown={handleMouseDown} onMouseUp={t20} onPaste={handlePaste} role="textbox" tabIndex={0}>{t21}</T0>;
    $[70] = T0;
    $[71] = handleKeyDown;
    $[72] = handleMouseDown;
    $[73] = handlePaste;
    $[74] = placeholder;
    $[75] = submit;
    $[76] = t14;
    $[77] = t15;
    $[78] = t16;
    $[79] = t17;
    $[80] = t19;
    $[81] = t21;
    $[82] = t22;
  } else {
    t22 = $[82];
  }
  return t22;
};
export default EditableText;
export type { EditableTextProps, SubmitReason };
export { serializeEditableText };
function _temp(key) {
  return key + 1;
}
function _temp2(line) {
  return line.trim();
}
function _temp3(line_0) {
  return line_0.replace(LIST_MARKER, "");
}
function _temp4(line_1) {
  return line_1.trim();
}